import * as React from "react";

//inner components
import { Link, LinkProps, Text, TextProps } from "../../atoms";
import { Avatar, AvatarSize } from "../Avatar/index";

export type CommonAvatarProfileProps = {
	/**
	 * The ID of the employee
	 */
	employeeId: string;
	/**
	 * The name of the employee
	 */
	employeeName: string;
	/**
	 * The size of the avatar
	 * @default 20
	 */
	avatarSize?: AvatarSize;
	/**
	 * The size of the text typography
	 * @default paragraph-3
	 */
	textSize?: TextProps["size"];
	/**
	 * Font weight
	 * @default regular
	 */
	textWeight?: TextProps["weight"];

	/**
	 * The color of the text
	 * @default neutral-9
	 **/
	textColor?: TextProps["color"];
	/**
	 * Optional test ID for the link
	 */
	"data-testid"?: string;
	/**
	 * Optional test ID for the avatar element
	 */
	avatarTestId?: string;
	/**
	 * Optional class name to apply to the link
	 */
	dangerouslySetClassName?: string;
	/**
	 * Optional class name to apply to the inner avatar
	 */
	dangerouslySetInnerAvatarClassname?: string;
};

export type AvatarProfileProps =
	| (CommonAvatarProfileProps & {
			/**
			 * If true, the link will not be rendered
			 */
			noLink: true;
	  })
	| (CommonAvatarProfileProps & {
			/**
			 * If true, the link will not be rendered
			 */
			noLink?: false;
			/**
			 * If true, the link will open in a new tab
			 */
			isExternal?: LinkProps["isExternal"];
			/**
			 * If true, the link will force a hard reload of the page
			 */
			forceHardReload?: LinkProps["forceHardReload"];
	  });

/**
 * Avatar profile used to display an avatar and employee name, it can link to the user's profile page, and truncates to 1 line.
 * If noLink is true, the link will not be rendered.
 * If isExternal is true, the link will open in a new tab.
 * If forceHardReload is true, the link will force a hard reload of the page.
 */

export const AvatarProfile = React.forwardRef<HTMLAnchorElement, AvatarProfileProps>((props, ref) => {
	const {
		employeeId,
		employeeName,
		avatarSize = 20,
		textSize = "paragraph-3",
		textColor = undefined,
		textWeight = "regular",
		dangerouslySetClassName,
		dangerouslySetInnerAvatarClassname,
		avatarTestId,
	} = props;

	if (props?.noLink) {
		return (
			<div
				data-testid={props["data-testid"]}
				className={`grid grid-cols-[auto,1fr] items-center gap-1 ${dangerouslySetClassName}`}
			>
				<Avatar
					size={avatarSize}
					id={employeeId}
					dangerouslySetClassName={dangerouslySetInnerAvatarClassname}
					testId={avatarTestId}
				/>
				<Text size={textSize} weight={textWeight} color={textColor} dangerouslySetClassName="line-clamp-1">
					{employeeName}
				</Text>
			</div>
		);
	}

	const { isExternal, forceHardReload } = props;

	return (
		<Link
			ref={ref}
			forceHardReload={forceHardReload}
			isExternal={isExternal}
			href={`/user/${employeeId}`}
			data-testid={props["data-testid"]}
			dangerouslySetClassName={`grid grid-cols-[auto,1fr] gap-1 items-center ${dangerouslySetClassName}`}
			underline="hover"
		>
			<Avatar
				size={avatarSize}
				id={employeeId}
				alt={`${employeeName}'s avatar`}
				dangerouslySetClassName={dangerouslySetInnerAvatarClassname}
				testId={avatarTestId}
			/>
			<Text size={textSize} weight={textWeight} color="primary-5" dangerouslySetClassName="line-clamp-1">
				{employeeName}
			</Text>
		</Link>
	);
});

AvatarProfile.displayName = "AvatarProfile";
